import { statusTokens, primaryShades, greyScaleTokens } from '../../theme/theme';

/**
 * Configurazione centralizzata degli stati dei cataloghi
 * Definisce colori, etichette e comportamenti per ogni stato
 */
export const CATALOG_STATUSES = {
    // Stati principali
    PUBLISHED: {
        key: 'Published',
        label: 'PUBLISHED',
        color: statusTokens.success, // Verde
        priority: 1,
        description: 'Catalogo pubblicato e accessibile',
        actions: ['modify', 'unpublish', 'delete', 'publish'],
        isActive: true
    },
    UNPUBLISHED: {
        key: 'Unpublished',
        label: 'UNPUBLISHED',
        color: statusTokens.warning, // Arancione
        priority: 2,
        description: 'Catalogo non pubblicato, non accessibile',
        actions: ['modify', 'republish', 'delete'],
        isActive: false
    },
    EXPIRED: {
        key: 'Expired',
        label: 'EXPIRED',
        color: statusTokens.error, // Rosso
        priority: 3,
        description: 'Catalogo scaduto, non più accessibile',
        actions: ['modify', 'delete'], // Rimossa republish per cataloghi scaduti
        isActive: false
    },
    DRAFT: {
        key: 'Draft',
        label: 'DRAFT',
        color: statusTokens.info, // Blu dal tema
        priority: 4,
        description: 'Bozza del catalogo in modifica',
        actions: ['modify', 'publish', 'delete'],
        isActive: false
    },
    // Stati futuri possibili
    PENDING: {
        key: 'Pending',
        label: 'PENDING',
        color: primaryShades.primary400, // Viola dal tema
        priority: 5,
        description: 'In attesa di approvazione',
        actions: ['modify', 'delete'],
        isActive: false
    },
    MAINTENANCE: {
        key: 'Maintenance',
        label: 'MAINTENANCE',
        color: greyScaleTokens.grey500, // Grigio dal tema
        priority: 6,
        description: 'In manutenzione',
        actions: ['modify', 'delete'],
        isActive: false
    }
};

/**
 * Ottiene la configurazione dello stato di un catalogo
 * @param {Object} catalogue - Il catalogo
 * @returns {Object} Configurazione dello stato
 */
export const getCatalogStatus = (catalogue) => {
    if (!catalogue) {
        return CATALOG_STATUSES.PUBLISHED; // Default fallback
    }

    // Se è un draft, restituisce sempre DRAFT
    if (catalogue.isDraft) {
        return CATALOG_STATUSES.DRAFT;
    }

    // Cerca lo stato in base al campo status
    const statusKey = catalogue.status;
    const statusConfig = Object.values(CATALOG_STATUSES).find(
        status => status.key === statusKey
    );

    // Se lo stato non è riconosciuto, usa Published come default
    return statusConfig || CATALOG_STATUSES.PUBLISHED;
};

/**
 * Ottiene il colore per uno stato specifico
 * @param {string} statusKey - La chiave dello stato
 * @returns {string} Il colore esadecimale
 */
export const getStatusColor = (statusKey) => {
    const status = Object.values(CATALOG_STATUSES).find(
        s => s.key === statusKey || s.label === statusKey
    );
    return status?.color || CATALOG_STATUSES.PUBLISHED.color;
};

/**
 * Verifica se un catalogo è attivo (accessibile agli utenti)
 * @param {Object} catalogue - Il catalogo
 * @returns {boolean} True se il catalogo è attivo
 */
export const isCatalogActive = (catalogue) => {
    const status = getCatalogStatus(catalogue);
    return status.isActive;
};

/**
 * Ottiene le azioni disponibili per un catalogo in base al suo stato
 * @param {Object} catalogue - Il catalogo
 * @returns {Array} Array delle azioni disponibili
 */
export const getAvailableActions = (catalogue) => {
    const status = getCatalogStatus(catalogue);
    return status.actions || [];
};

/**
 * Verifica se un'azione è disponibile per un catalogo
 * @param {Object} catalogue - Il catalogo
 * @param {string} action - L'azione da verificare
 * @returns {boolean} True se l'azione è disponibile
 */
export const isActionAvailable = (catalogue, action) => {
    const availableActions = getAvailableActions(catalogue);
    return availableActions.includes(action);
}; 